// EditShortcode.tsx
import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  addDoc,
  deleteDoc,
  query,
  orderBy
} from 'firebase/firestore';
import { dbModernCasinos, dbHrefsets } from '../../App';
import { useNotification } from '../../universal/context/NotificationContext';
import { useLoader } from '../../universal/context/LoaderContext';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { UserContext } from '../../universal/context/UserContext';
import RevisionsModal from '../modals/RevisionsModal';

// Define the casino interface (extend as needed)
interface ICasino {
  hasRibbon: boolean;
  ribbonText: string;
  ribbonType: string; // "default" or "new"
  position: number;
  image: string;
  imageAlt?: string;
  bonus: string;
  term1?: string;
  term2?: string;
  term3?: string;
  termsPlain: string;
  termsLinks: string[];
  gotoLink: string;
  trackingLink?: string;
  firebaseImageUrl?: string;
  mainTermsText?: string; // New field added per casino row
  // 'deleted' is intentionally not part of ICasino so it won't be sent in updates
}

// Extend with the document id for editing
interface ICasinoWithId extends ICasino {
  id: string;
  deleted: boolean;
}

// Define a type for temporary image storage per casino
interface ITempImage {
  file: File;
  preview: string;
}

const randomImages = [
  "https://via.placeholder.com/100?text=Casino+1",
  "https://via.placeholder.com/100?text=Casino+2",
  "https://via.placeholder.com/100?text=Casino+3"
];

const EditShortcode: React.FC = () => {
  const { id } = useParams<{ id: string }>(); // id represents the casinoset (and site) id
  const { user } = useContext(UserContext);
  const [casinos, setCasinos] = useState<ICasinoWithId[]>([]);
  const [originalCasinos, setOriginalCasinos] = useState<ICasinoWithId[]>([]);
  const [posInputs, setPosInputs] = useState<{ [id: string]: string }>({});
  const [posErrors, setPosErrors] = useState<{ [id: string]: string }>({});
  const [isReorganized, setIsReorganized] = useState<boolean>(false);
  const [tempImages, setTempImages] = useState<{ [id: string]: ITempImage }>({});

  // Header texts and button labels
  const [headerTexts, setHeaderTexts] = useState({
    posText: '',
    casinoText: '',
    casinoBonusText: '',
    moreInfoText: '',
    moveOnText: ''
  });
  const [ctaButtonText, setCtaButtonText] = useState('');
  const [loadMoreButtonText, setLoadMoreButtonText] = useState('');

  // NEW: State for GEO Slug and Page Slug
  const [geoSlug, setGeoSlug] = useState('');
  const [pageSlug, setPageSlug] = useState('');

  // Control revisions modal visibility
  const [showRevisionsModal, setShowRevisionsModal] = useState<boolean>(false);

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const storage = getStorage();

  useEffect(() => {
    if (id) {
      loadParentData();
      loadCasinos();
    }
  }, [id]);

  const loadParentData = async () => {
    try {
      const parentDocRef = doc(dbModernCasinos, 'casinosets', id!);
      const docSnap = await getDoc(parentDocRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setHeaderTexts({
          posText: data.headerTexts?.posText || '',
          casinoText: data.headerTexts?.casinoText || '',
          casinoBonusText: data.headerTexts?.casinoBonusText || '',
          moreInfoText: data.headerTexts?.moreInfoText || '',
          moveOnText: data.headerTexts?.moveOnText || ''
        });
        setCtaButtonText(data.ctaButtonText || '');
        setLoadMoreButtonText(data.loadMoreButtonText || '');
        // NEW: Set GEO Slug and Page Slug from parent document if available
        setGeoSlug(data.geoSlug || '');
        setPageSlug(data.pageSlug || '');
      }
    } catch (error: any) {
      console.error("Error loading parent document:", error);
      showNotification(`Error loading header fields: ${error.message}`, "error");
    }
  };

  const loadCasinos = async () => {
    try {
      showLoader("Loading casinos...");
      const casinosRef = collection(dbModernCasinos, 'casinosets', id!, 'casinos');
      const qs = await getDocs(casinosRef);
      const data = qs.docs.map(docSnap => {
        const casinoData = docSnap.data() as ICasino & { deleted?: boolean };
        return {
          id: docSnap.id,
          ...casinoData,
          deleted: casinoData.deleted ?? false
        } as ICasinoWithId;
      });
      const sortedData = data.sort((a, b) => a.position - b.position);
      setCasinos(sortedData);
      setOriginalCasinos(sortedData);
      const newPosInputs: { [id: string]: string } = {};
      sortedData.forEach(casino => {
        newPosInputs[casino.id] = casino.position.toString();
      });
      setPosInputs(newPosInputs);
      setIsReorganized(false);
      hideLoader();
    } catch (error: any) {
      console.error("Error loading casinos:", error);
      showNotification(`Error loading casinos: ${error.message}`, 'error');
      hideLoader();
    }
  };

  const uploadNewImage = async (file: File, casino: ICasinoWithId): Promise<string> => {
    try {
      const newImageRef = ref(storage, `mcasinos/${file.name}`);
      await uploadBytes(newImageRef, file);
      const newUrl = await getDownloadURL(newImageRef);
      return newUrl;
    } catch (error: any) {
      throw new Error(`Image upload failed: ${error.message}`);
    }
  };

  const handleFieldChange = (index: number, field: keyof ICasino, value: any) => {
    const newCasinos = [...casinos];
    newCasinos[index] = { ...newCasinos[index], [field]: value };
    setCasinos(newCasinos);
  };

  const handleToggleDelete = (index: number, value: boolean) => {
    const newCasinos = [...casinos];
    newCasinos[index] = { ...newCasinos[index], deleted: value };
    setCasinos(newCasinos);
  };

  const isEqual = (a: any, b: any): boolean => {
    if (Array.isArray(a) && Array.isArray(b)) {
      return JSON.stringify(a) === JSON.stringify(b);
    }
    return a === b;
  };

  const isFieldModified = (casino: ICasinoWithId, field: keyof ICasino): boolean => {
    const orig = originalCasinos.find(c => c.id === casino.id);
    if (!orig) return false;
    return !isEqual(casino[field], orig[field]);
  };

  const validatePosition = (casinoId: string, rawValue: string, index: number) => {
    const trimmed = rawValue.trim();
    const num = Number(trimmed);
    let error = "";
    if (trimmed === "" || isNaN(num)) {
      error = "Can't save words in position";
    } else if (num <= 0 || num === -1) {
      error = "Position must be > 0 and not -1";
    }
    setPosErrors(prev => ({ ...prev, [casinoId]: error }));
    if (error) {
      showNotification(error, "error");
    } else {
      const newCasinos = casinos.map((casino, i) =>
        i === index ? { ...casino, position: num } : casino
      );
      setCasinos(newCasinos);
      setIsReorganized(false);
    }
  };

  const hasChanges = casinos.some(casino => {
    const orig = originalCasinos.find(c => c.id === casino.id);
    if (orig) {
      if (casino.deleted !== orig.deleted) return true;
    } else {
      if (casino.deleted) return true;
      return true;
    }
    const modifiableFields: (keyof ICasino)[] = [
      'hasRibbon',
      'ribbonText',
      'ribbonType',
      'position',
      'image',
      'imageAlt',
      'bonus',
      'term1',
      'term2',
      'term3',
      'termsPlain',
      'termsLinks',
      'gotoLink',
      'trackingLink',
      'firebaseImageUrl',
      'mainTermsText'
    ];
    if (modifiableFields.some(field => !isEqual(casino[field], orig[field]))) return true;
    return !!tempImages[casino.id];
  });

  const hasPosChange = casinos.filter(c => !c.deleted).some(casino => {
    const orig = originalCasinos.find(c => c.id === casino.id);
    if (!orig) return false;
    return casino.position !== orig.position;
  });

  const handleReorganize = () => {
    const updated = casinos.map(casino => ({
      ...casino,
      target: Number(posInputs[casino.id]) || casino.position
    }));
    const sorted = updated.sort((a, b) => {
      if (a.target === b.target) {
        const origA = originalCasinos.find(c => c.id === a.id);
        const origB = originalCasinos.find(c => c.id === b.id);
        const manualA = origA ? Number(posInputs[a.id]) !== origA.position : false;
        const manualB = origB ? Number(posInputs[b.id]) !== origB.position : false;
        if (manualA && !manualB) return 1;
        if (!manualA && manualB) return -1;
        return 0;
      }
      return a.target - b.target;
    });
    const finalOrder = sorted.map((casino, index) => ({
      ...casino,
      position: index + 1
    }));
    setCasinos(finalOrder);
    const newPosInputs: { [id: string]: string } = {};
    finalOrder.forEach(casino => {
      newPosInputs[casino.id] = casino.position.toString();
    });
    setPosInputs(newPosInputs);
    setIsReorganized(true);
    showNotification(`${finalOrder.length} casinos re-ordered`, "success");
  };

  const handleImageSelection = (e: React.ChangeEvent<HTMLInputElement>, casinoId: string) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setTempImages(prev => ({ ...prev, [casinoId]: { file, preview } }));
  };

  const handleAddCasino = () => {
    const newCasino: ICasinoWithId = {
      id: `new-${Date.now()}`,
      hasRibbon: false,
      ribbonText: '',
      ribbonType: 'default',
      position: 1,
      image: randomImages[Math.floor(Math.random() * randomImages.length)],
      imageAlt: '',
      bonus: '',
      term1: '',
      term2: '',
      term3: '',
      termsPlain: '',
      termsLinks: [],
      gotoLink: '',
      trackingLink: '',
      firebaseImageUrl: '',
      mainTermsText: '',
      deleted: false
    };
    const newCasinos = [newCasino, ...casinos];
    newCasinos.forEach((casino, index) => {
      casino.position = index + 1;
    });
    setCasinos(newCasinos);
    const newPosInputs = newCasinos.reduce((acc: { [id: string]: string }, casino) => {
      acc[casino.id] = casino.position.toString();
      return acc;
    }, {});
    setPosInputs(newPosInputs);
  };

  const handleSave = async () => {
    if (Object.values(posErrors).some(err => err)) {
      showNotification("Fix position errors before saving.", "error");
      return;
    }
    try {
      showLoader("Saving changes...");
      const casinosToSave = [...casinos];
      const activeCasinos = casinosToSave.filter(c => !c.deleted).sort((a, b) => a.position - b.position);
      activeCasinos.forEach((casino, index) => {
        casino.position = index + 1;
      });
      const casinosRef = collection(dbModernCasinos, 'casinosets', id!, 'casinos');
      for (const casino of casinosToSave) {
        let updatedCasino = { ...casino };
        if (tempImages[casino.id]) {
          const newUrl = await uploadNewImage(tempImages[casino.id].file, casino);
          updatedCasino.firebaseImageUrl = newUrl;
        }
        const original = originalCasinos.find(c => c.id === casino.id);
        if (casino.deleted) {
          if (original) {
            const casinoDocRef = doc(dbModernCasinos, 'casinosets', id!, 'casinos', casino.id);
            await deleteDoc(casinoDocRef);
          }
          continue;
        } else {
          if (original) {
            const diff: Partial<ICasino> = {};
            const modifiableFields: (keyof ICasino)[] = [
              'hasRibbon',
              'ribbonText',
              'ribbonType',
              'position',
              'image',
              'imageAlt',
              'bonus',
              'term1',
              'term2',
              'term3',
              'termsPlain',
              'termsLinks',
              'gotoLink',
              'trackingLink',
              'firebaseImageUrl',
              'mainTermsText'
            ];
            modifiableFields.forEach(field => {
              if (!isEqual(updatedCasino[field], original[field])) {
                (diff as any)[field] = updatedCasino[field];
              }
            });
            if (Object.keys(diff).length > 0) {
              const casinoDocRef = doc(dbModernCasinos, 'casinosets', id!, 'casinos', casino.id);
              await updateDoc(casinoDocRef, diff);
            }
          } else {
            const { id, deleted, ...newCasinoData } = updatedCasino;
            await addDoc(casinosRef, newCasinoData);
          }
        }
      }

      // Store a revision identical to the normal data.
      const parentDocRef = doc(dbModernCasinos, 'casinosets', id!);
      const parentDocSnap = await getDoc(parentDocRef);
      const parentData = parentDocSnap.data() || {};
      const revisionData = {
        timestamp: new Date(),
        storedBy: user?.name || 'Unknown',
        parentData, // complete parent data including new fields
        casinosData: casinos // complete casinos array snapshot
      };

      // Enforce a maximum of 10 revisions.
      const revisionsRef = collection(dbModernCasinos, 'casinosets', id!, 'revisions');
      const q = query(revisionsRef, orderBy('timestamp', 'asc'));
      const revSnap = await getDocs(q);
      if (revSnap.docs.length >= 10) {
        const numToDelete = revSnap.docs.length - 9;
        for (let i = 0; i < numToDelete; i++) {
          await deleteDoc(revSnap.docs[i].ref);
        }
      }
      await addDoc(revisionsRef, revisionData);

      const newSaveId = Math.random().toString(36).substr(2, 9);
      await updateDoc(parentDocRef, {
        saveId: newSaveId,
        headerTexts,
        ctaButtonText,
        loadMoreButtonText,
        // NEW: Update the GEO Slug and Page Slug fields
        geoSlug,
        pageSlug
      });

      hideLoader();
      showNotification("Casinos updated successfully!", "success");
      loadCasinos();
      setTempImages({});
    } catch (error: any) {
      hideLoader();
      console.error("Error saving casinos:", error);
      showNotification(`Error saving casinos: ${error.message}`, "error");
    }
  };

  const getCellStyle = (fieldName: keyof ICasino, casino: ICasinoWithId, isPos?: boolean) => {
    if (fieldName === 'image' && tempImages[casino.id]) {
      return { backgroundColor: '#ffcd71' };
    }
    if (isPos && posErrors[casino.id]) {
      return { backgroundColor: '#ff7171' };
    }
    if (isFieldModified(casino, fieldName)) {
      return { backgroundColor: '#ffcd71' };
    }
    return {};
  };

  return (
    <div className="dashpanel">
      <div className="main">
        <div className="head">
          <h2>Edit Shortcode: {id}</h2>
          <div style={{ marginBottom: '1rem', marginTop: '1rem', display: 'flex', gap: '10px' }}>
            <Link
              to="/console/shortcodes"
              style={{
                backgroundColor: '#007BFF',
                color: '#fff',
                fontWeight: 'bold',
                padding: '10px 15px',
                borderRadius: '5px',
                textDecoration: 'none'
              }}
            >
              Back to Shortcodes
            </Link>
            <button
              onClick={() => setShowRevisionsModal(true)}
              style={{
                backgroundColor: '#6c757d',
                color: '#fff',
                fontWeight: 'bold',
                padding: '10px 15px',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              Revisions
            </button>
          </div>
          {/* Parent Document Fields */}
          <div style={{ marginBottom: '1rem', border: '1px solid #ccc', padding: '10px' }}>
            <h3>Header Fields</h3>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <label>Pos Text</label>
              <input
                type="text"
                value={headerTexts.posText}
                onChange={(e) => setHeaderTexts({ ...headerTexts, posText: e.target.value })}
                placeholder="POS"
                style={{ minWidth: '150px' }}
              />
              <label>Casino Text</label>
              <input
                type="text"
                value={headerTexts.casinoText}
                onChange={(e) => setHeaderTexts({ ...headerTexts, casinoText: e.target.value })}
                placeholder="CASINO"
                style={{ minWidth: '150px' }}
              />
              <label>Casino Bonus Text</label>
              <input
                type="text"
                value={headerTexts.casinoBonusText}
                onChange={(e) => setHeaderTexts({ ...headerTexts, casinoBonusText: e.target.value })}
                placeholder="CASINO BONUS"
                style={{ minWidth: '150px' }}
              />
              <label>More Info Text</label>
              <input
                type="text"
                value={headerTexts.moreInfoText}
                onChange={(e) => setHeaderTexts({ ...headerTexts, moreInfoText: e.target.value })}
                placeholder="MORE INFO"
                style={{ minWidth: '150px' }}
              />
              <label>Move on Text</label>
              <input
                type="text"
                value={headerTexts.moveOnText}
                onChange={(e) => setHeaderTexts({ ...headerTexts, moveOnText: e.target.value })}
                placeholder="MOVE ON"
                style={{ minWidth: '150px' }}
              />
            </div>
            {/* NEW: GEO Slug and Page Slug Fields */}
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1rem' }}>
            <label>GEO Slug Text</label>
              <input
              disabled
                type="text"
                value={geoSlug}
                onChange={(e) => setGeoSlug(e.target.value)}
                placeholder="GEO Slug (ex: de)"
                style={{ minWidth: '150px' }}
              />
              <label>PAGE Slug Text</label>
              <input
              disabled
                type="text"
                value={pageSlug}
                onChange={(e) => setPageSlug(e.target.value)}
                placeholder="Page Slug (ex: free)"
                style={{ minWidth: '150px' }}
              />
            </div>
            <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <div>
                <label>CTA Button Text:</label>
                <input
                  type="text"
                  value={ctaButtonText}
                  onChange={(e) => setCtaButtonText(e.target.value)}
                  placeholder="CTA Button Text"
                  style={{ minWidth: '150px' }}
                />
              </div>
              <div>
                <label>Load More Button Text:</label>
                <input
                  type="text"
                  value={loadMoreButtonText}
                  onChange={(e) => setLoadMoreButtonText(e.target.value)}
                  placeholder="Load More Button Text"
                  style={{ minWidth: '150px' }}
                />
              </div>
            </div>
          </div>
          {/* Add Casino Button */}
          <div style={{ marginBottom: '1rem' }}>
            <button
              onClick={handleAddCasino}
              style={{
                backgroundColor: '#28a745',
                color: '#fff',
                fontWeight: 'bold',
                padding: '10px 15px',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              Add Casino
            </button>
          </div>
          {casinos.length === 0 ? (
            <p>No casinos found for this shortcode.</p>
          ) : (
            <div style={{ overflowY: 'auto', maxHeight: '70vh', marginRight: '5%' }}>
              <table
                className="casino-table"
                style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px' }}
              >
                <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 0 }}>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th>Ribbon?</th>
                    <th>Ribbon Text</th>
                    <th>Ribbon Type</th>
                    <th>Position</th>
                    <th>Image</th>
                    <th>Alt Text</th>
                    <th>Bonus</th>
                    <th>Term 1</th>
                    <th>Term 2</th>
                    <th>Term 3</th>
                    <th>Terms (Plain)</th>
                    <th>Terms Link(s)</th>
                    <th>Main Terms Text</th>
                    <th>Goto Link</th>
                    <th>Tracking Link</th>
                    <th>Delete?</th>
                  </tr>
                </thead>
                <tbody>
                  {casinos.map((casino, index) => (
                    <tr key={casino.id} style={{ backgroundColor: casino.deleted ? '#ffcccc' : undefined }}>
                      <td style={getCellStyle('hasRibbon', casino)}>
                        <input
                          type="checkbox"
                          checked={casino.hasRibbon}
                          onChange={(e) => handleFieldChange(index, 'hasRibbon', e.target.checked)}
                          style={{ minWidth: '100px' }}
                        />
                      </td>
                      <td style={getCellStyle('ribbonText', casino)}>
                        <input
                          type="text"
                          value={casino.ribbonText}
                          onChange={(e) => handleFieldChange(index, 'ribbonText', e.target.value)}
                          style={{ minWidth: '150px' }}
                        />
                      </td>
                      <td style={getCellStyle('ribbonType', casino)}>
                        <select
                          value={casino.ribbonType}
                          onChange={(e) => handleFieldChange(index, 'ribbonType', e.target.value)}
                          style={{ minWidth: '150px', padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                        >
                          <option value="default">default</option>
                          <option value="new">new</option>
                        </select>
                      </td>
                      <td style={getCellStyle('position', casino, true)}>
                        <input
                          type="text"
                          value={posInputs[casino.id] || ''}
                          onChange={(e) => {
                            setPosInputs({ ...posInputs, [casino.id]: e.target.value });
                            if (posErrors[casino.id]) {
                              setPosErrors({ ...posErrors, [casino.id]: "" });
                            }
                            setIsReorganized(false);
                          }}
                          onBlur={(e) => validatePosition(casino.id, e.target.value, index)}
                          style={{ minWidth: '100px' }}
                        />
                      </td>
                      <td style={getCellStyle('image', casino)}>
                        <div
                          onClick={() => document.getElementById(`fileInput-${casino.id}`)?.click()}
                          style={{ cursor: 'pointer' }}
                        >
                          <img
                            src={
                              tempImages[casino.id]?.preview ||
                              casino.firebaseImageUrl ||
                              casino.image
                            }
                            alt={casino.imageAlt || 'Casino'}
                            style={{ maxWidth: '100px', borderRadius: '4px' }}
                          />
                        </div>
                        <input
                          id={`fileInput-${casino.id}`}
                          type="file"
                          style={{ display: 'none' }}
                          accept="image/*"
                          onChange={(e) => handleImageSelection(e, casino.id)}
                        />
                      </td>
                      <td style={getCellStyle('imageAlt', casino)}>
                        <input
                          type="text"
                          value={casino.imageAlt || ''}
                          onChange={(e) => handleFieldChange(index, 'imageAlt', e.target.value)}
                          style={{ minWidth: '150px' }}
                        />
                      </td>
                      <td style={getCellStyle('bonus', casino)}>
                        <input
                          type="text"
                          value={casino.bonus}
                          onChange={(e) => handleFieldChange(index, 'bonus', e.target.value)}
                          style={{ minWidth: '150px' }}
                        />
                      </td>
                      <td style={getCellStyle('term1', casino)}>
                        <input
                          type="text"
                          value={casino.term1 || ''}
                          onChange={(e) => handleFieldChange(index, 'term1', e.target.value)}
                          style={{ minWidth: '150px' }}
                        />
                      </td>
                      <td style={getCellStyle('term2', casino)}>
                        <input
                          type="text"
                          value={casino.term2 || ''}
                          onChange={(e) => handleFieldChange(index, 'term2', e.target.value)}
                          style={{ minWidth: '150px' }}
                        />
                      </td>
                      <td style={getCellStyle('term3', casino)}>
                        <input
                          type="text"
                          value={casino.term3 || ''}
                          onChange={(e) => handleFieldChange(index, 'term3', e.target.value)}
                          style={{ minWidth: '150px' }}
                        />
                      </td>
                      <td style={getCellStyle('termsPlain', casino)}>
                        <input
                          type="text"
                          value={casino.termsPlain}
                          onChange={(e) => handleFieldChange(index, 'termsPlain', e.target.value)}
                          style={{ minWidth: '150px' }}
                        />
                      </td>
                      <td style={getCellStyle('termsLinks', casino)}>
                        <input
                          type="text"
                          value={casino.termsLinks.join(', ')}
                          onChange={(e) =>
                            handleFieldChange(
                              index,
                              'termsLinks',
                              e.target.value.split(',').map(link => link.trim())
                            )
                          }
                          style={{ minWidth: '150px' }}
                        />
                      </td>
                      <td style={getCellStyle('mainTermsText', casino)}>
                        <input
                          type="text"
                          value={casino.mainTermsText || ''}
                          onChange={(e) => handleFieldChange(index, 'mainTermsText', e.target.value)}
                          style={{ minWidth: '150px' }}
                        />
                      </td>
                      <td style={getCellStyle('gotoLink', casino)}>
                        <input
                          type="text"
                          value={casino.gotoLink}
                          onChange={(e) => handleFieldChange(index, 'gotoLink', e.target.value)}
                          style={{ minWidth: '150px' }}
                        />
                      </td>
                      <td style={getCellStyle('trackingLink', casino)}>
                        <input
                          type="text"
                          value={casino.trackingLink || ''}
                          onChange={(e) => handleFieldChange(index, 'trackingLink', e.target.value)}
                          style={{ minWidth: '300px', overflow: 'auto' }}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={casino.deleted}
                          onChange={(e) => handleToggleDelete(index, e.target.checked)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {hasChanges && (
            <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 1000 }}>
              {hasPosChange && !isReorganized ? (
                <button
                  onClick={handleReorganize}
                  style={{
                    backgroundColor: '#ff9800',
                    color: '#fff',
                    fontWeight: 'bold',
                    padding: '10px 15px',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  Re-organize
                </button>
              ) : (
                <button
                  onClick={handleSave}
                  disabled={Object.values(posErrors).some(err => err)}
                  style={{
                    backgroundColor: Object.values(posErrors).some(err => err) ? 'gray' : '#007BFF',
                    color: '#fff',
                    fontWeight: 'bold',
                    padding: '10px 15px',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: Object.values(posErrors).some(err => err) ? 'not-allowed' : 'pointer'
                  }}
                >
                  Save Changes
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {showRevisionsModal && (
        <RevisionsModal
          parentId={id!}
          onClose={() => setShowRevisionsModal(false)}
        />
      )}
    </div>
  );
};

export default EditShortcode;
